import { Fragment, useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronRight, Plus, X } from 'lucide-react';
import type { ReaderExtraction, ReadingDraft } from '../services/readings';
import { reportConfirmation, saveReadings } from '../services/readings';
import { confirmCapture } from '../services/captures';
import { Badge, Button, Input, cx } from './ui';

/**
 * Confirming readings against a reader.
 *
 * One card per reading collapses once a sheet has more than a dozen rows —
 * nobody checks them all; they scroll to the bottom and press confirm. So this
 * groups by sample, the unit a person actually thinks in, and shows each
 * sample's replicates together, where an outlier is visible at a glance. The
 * name is editable because that is what the model gets wrong in ways that
 * matter; values are editable too.
 *
 * With `manual`, the same screen is typed entry: it starts with one blank
 * sample, and samples and replicates can be added by hand.
 */
export default function ReaderConfirmation({
  projectId,
  captureId,
  uid,
  extraction,
  onDone,
  onCancel,
  manual = false,
  resolveCaptureId,
}: {
  projectId: string;
  captureId: string | null;
  uid: string;
  extraction: ReaderExtraction;
  onDone: (count: number) => void;
  onCancel: () => void;
  /** Typed entry: blank to start, samples and replicates added by hand. */
  manual?: boolean;
  /**
   * Makes the capture record at save time, for entries that have none yet.
   * Typed entry uses it so that cancelling leaves nothing behind in History.
   */
  resolveCaptureId?: () => Promise<string>;
}) {
  const fields = extraction.reader?.fields ?? [];

  /** Working copy. Nothing is written until Confirm. */
  const [rows, setRows] = useState(() =>
    extraction.readings.map((r, i) => ({
      localId: `r${i}`,
      sampleLabel: r.sampleLabel,
      labelParts: r.labelParts?.length ? r.labelParts : [r.sampleLabel],
      replicate: r.replicate,
      values: { ...r.values },
      /**
       * What is in each box, as typed. Kept apart from `values` because "1."
       * and "-" are not numbers yet — parsing every keystroke threw away the
       * decimal point and the minus sign before the next digit could follow.
       */
      text: Object.fromEntries(Object.entries(r.values).map(([k, v]) => [k, String(v)])) as Record<
        string,
        string
      >,
      /** Summary sheets: SD per field and N per sample, typed the same way. */
      sd: { ...(r.sd ?? {}) } as Record<string, number>,
      sdText: Object.fromEntries(Object.entries(r.sd ?? {}).map(([k, v]) => [k, String(v)])) as Record<
        string,
        string
      >,
      n: typeof r.n === 'number' ? r.n : null,
      nText: typeof r.n === 'number' ? String(r.n) : '',
      dropped: false,
    })),
  );

  /**
   * Averages with SD rather than individual readings. Read from the sheet, or
   * chosen by hand when typing. A summary needs N — without it an SD cannot be
   * compared with anything — so it is asked for wherever the sheet left it out.
   */
  const [summary, setSummary] = useState(
    () =>
      extraction.dataShape === 'summary' ||
      extraction.readings.some((r) => r.sd && Object.keys(r.sd).length > 0),
  );
  const [openSample, setOpenSample] = useState<string | null>(() =>
    manual ? (extraction.readings[0]?.sampleLabel ?? null) : null,
  );
  const [nextId, setNextId] = useState(extraction.readings.length);

  /** A name not already on screen, so a new sample never merges into another. */
  function freshLabel(): string {
    const taken = new Set(rows.map((r) => r.sampleLabel));
    for (let n = taken.size + 1; ; n++) {
      if (!taken.has(`Sample ${n}`)) return `Sample ${n}`;
    }
  }

  function addSample() {
    const label = freshLabel();
    setRows((rs) => [
      ...rs,
      { localId: `r${nextId}`, sampleLabel: label, labelParts: [label], replicate: 1, values: {}, text: {}, sd: {}, sdText: {}, n: null, nText: '', dropped: false },
    ]);
    setNextId((n) => n + 1);
    setOpenSample(label);
  }

  function addReplicate(label: string) {
    setRows((rs) => {
      const reps = rs.filter((r) => r.sampleLabel === label);
      const last = reps[reps.length - 1];
      const replicate = Math.max(0, ...reps.map((r) => r.replicate)) + 1;
      return [
        ...rs,
        {
          localId: `r${nextId}`,
          sampleLabel: label,
          labelParts: last?.labelParts ?? [label],
          replicate,
          values: {},
          text: {},
          sd: {},
          sdText: {},
          n: null,
          nText: '',
          dropped: false,
        },
      ];
    });
    setNextId((n) => n + 1);
  }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** One entry per sample, in the order the sheet presented them. */
  const samples = useMemo(() => {
    const order: string[] = [];
    const bySample = new Map<string, typeof rows>();
    for (const r of rows) {
      if (!bySample.has(r.sampleLabel)) {
        bySample.set(r.sampleLabel, []);
        order.push(r.sampleLabel);
      }
      bySample.get(r.sampleLabel)!.push(r);
    }
    return order.map((label) => ({ label, rows: bySample.get(label)! }));
  }, [rows]);

  const kept = rows.filter((r) => !r.dropped);
  /** Only rows with at least one value are saved; a blank row is ignored. */
  const filled = kept.filter((r) => Object.keys(r.values).length > 0).length;
  /** Averages still waiting for their N. */
  const missingN = summary
    ? [
        ...new Set(
          kept
            .filter((r) => Object.keys(r.values).length > 0 && r.n === null)
            .map((r) => r.sampleLabel),
        ),
      ]
    : [];

  /** Renaming a sample renames every replicate under it, which is the point. */
  function renameSample(from: string, to: string) {
    setRows((rs) =>
      rs.map((r) =>
        r.sampleLabel === from
          ? {
              ...r,
              sampleLabel: to,
              // The last part is the one a person is editing; outer levels are
              // the grouping and stay as the sheet had them.
              labelParts: r.labelParts.length > 1 ? [...r.labelParts.slice(0, -1), to] : [to],
            }
          : r,
      ),
    );
    setOpenSample((s) => (s === from ? to : s));
  }

  function setValue(localId: string, key: string, raw: string) {
    setRows((rs) =>
      rs.map((r) => {
        if (r.localId !== localId) return r;
        const next = { ...r.values };
        const n = Number(raw.trim().replace(',', '.'));
        if (raw.trim() === '' || !Number.isFinite(n)) delete next[key];
        else next[key] = n;
        return { ...r, values: next, text: { ...r.text, [key]: raw } };
      }),
    );
  }

  function setSd(localId: string, key: string, raw: string) {
    setRows((rs) =>
      rs.map((r) => {
        if (r.localId !== localId) return r;
        const next = { ...r.sd };
        const v = Number(raw.trim().replace(',', '.'));
        if (raw.trim() === '' || !Number.isFinite(v) || v < 0) delete next[key];
        else next[key] = v;
        return { ...r, sd: next, sdText: { ...r.sdText, [key]: raw } };
      }),
    );
  }

  function setN(localId: string, raw: string) {
    const v = Number(raw.trim());
    setRows((rs) =>
      rs.map((r) =>
        r.localId === localId
          ? { ...r, nText: raw, n: Number.isInteger(v) && v > 0 ? v : null }
          : r,
      ),
    );
  }

  /** Typed entry can switch shape; a summary keeps one row per sample. */
  function chooseSummary(on: boolean) {
    setSummary(on);
    if (on) {
      setRows((rs) => {
        const seen = new Set<string>();
        return rs.filter((r) => (seen.has(r.sampleLabel) ? false : (seen.add(r.sampleLabel), true)));
      });
    }
  }

  function toggleSample(label: string, dropped: boolean) {
    setRows((rs) => rs.map((r) => (r.sampleLabel === label ? { ...r, dropped } : r)));
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const drafts: ReadingDraft[] = kept
        .filter((r) => Object.keys(r.values).length > 0)
        .map((r) => ({
          sampleLabel: r.sampleLabel,
          labelParts: r.labelParts,
          replicate: r.replicate,
          values: r.values,
          ...(summary
            ? {
                summary: true,
                // An SD without its average means nothing; keep only paired ones.
                sd: Object.fromEntries(Object.entries(r.sd).filter(([k]) => k in r.values)),
                n: r.n,
              }
            : {}),
        }));

      const id = captureId ?? (resolveCaptureId ? await resolveCaptureId() : null);
      const count = await saveReadings(
        projectId,
        id,
        uid,
        { id: extraction.readerId, name: extraction.reader?.name ?? '' },
        drafts,
      );

      // The capture is settled: without this it would sit in History as pending
      // forever. Its readings live in their own collection, not on formulations.
      if (id) await confirmCapture(projectId, id, { readings: [] }, []);

      // What the person changed is the only trustworthy training signal there
      // is. Sent after the save, and never waited on. Typed entry teaches
      // nothing — there was no read to correct.
      if (!manual) reportConfirmation(extraction.readerId, extraction.readings, drafts);
      onDone(count);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not save those readings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-ink-800 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{extraction.reader?.name}</span>
          <Badge tone="info">
            {samples.length} sample{samples.length === 1 ? '' : 's'}
          </Badge>
          <Badge tone="neutral">
            {kept.length} reading{kept.length === 1 ? '' : 's'}
          </Badge>
          {!manual && (
            <>
              <Badge tone={extraction.confidence === 'low' ? 'bad' : 'neutral'}>
                {extraction.confidence} confidence
              </Badge>
              <span className="ml-auto text-xs text-ink-500">
                {extraction.orientation === 'samples-in-columns'
                  ? 'samples in columns'
                  : 'samples in rows'}
              </span>
            </>
          )}
        </div>

        {manual ? (
          <div className="mt-2 inline-flex rounded-lg bg-ink-800 p-0.5 text-xs">
            {[
              [false, 'Individual readings'],
              [true, 'Averages with SD and N'],
            ].map(([on, text]) => (
              <button
                key={String(on)}
                onClick={() => chooseSummary(on as boolean)}
                className={cx(
                  'rounded-md px-2.5 py-1 font-medium pointer-coarse:min-h-9',
                  summary === on ? 'bg-accent-500 text-white' : 'text-ink-400 hover:text-ink-100',
                )}
              >
                {text as string}
              </button>
            ))}
          </div>
        ) : (
          summary && (
            <p className="mt-2 text-xs text-ink-400">
              This sheet shows averages with SD. Each sample needs its N — how many measurements
              the average is from.
            </p>
          )
        )}

        {!manual && extraction.confidence === 'low' && (
          <p className="mt-2 flex gap-1.5 text-xs text-amber-300">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            Low confidence — check the names and spot-check the values before confirming.
          </p>
        )}
        {extraction.unreadable?.length > 0 && (
          <p className="mt-1.5 text-xs text-amber-300">
            Could not read: {extraction.unreadable.join(', ')}
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <ul className="space-y-2">
          {samples.map(({ label, rows: reps }) => {
            const open = openSample === label;
            const dropped = reps.every((r) => r.dropped);
            return (
              <li
                key={label}
                className={cx(
                  'rounded-lg border',
                  dropped ? 'border-ink-800 bg-ink-900/30 opacity-50' : 'border-ink-700 bg-ink-800/40',
                )}
              >
                <div className="flex items-center gap-2 p-2">
                  <button
                    onClick={() => setOpenSample(open ? null : label)}
                    className="shrink-0 text-ink-500 hover:text-ink-200"
                    aria-label={open ? 'Collapse' : 'Expand'}
                  >
                    <ChevronRight
                      size={15}
                      className={cx('transition-transform', open && 'rotate-90')}
                    />
                  </button>

                  <Input
                    value={label}
                    onChange={(e) => renameSample(label, e.target.value)}
                    className="min-w-0 flex-1 !py-1 font-mono !text-sm"
                    aria-label={`Name for ${label}`}
                  />

                  {summary ? (
                    <label className="flex shrink-0 items-center gap-1 text-xs text-ink-400">
                      N
                      <Input
                        value={reps[0].nText}
                        onChange={(e) => setN(reps[0].localId, e.target.value)}
                        placeholder="?"
                        className={cx(
                          '!w-12 !px-1.5 !py-0.5 text-center font-mono !text-xs',
                          !dropped &&
                            Object.keys(reps[0].values).length > 0 &&
                            reps[0].n === null &&
                            '!border-amber-500',
                        )}
                        aria-label={`N for ${label}`}
                      />
                    </label>
                  ) : (
                    <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-ink-400">
                      {reps.length}×
                    </span>
                  )}

                  <button
                    onClick={() => toggleSample(label, !dropped)}
                    className="icon-btn shrink-0 text-ink-400 hover:bg-ink-800"
                    title={dropped ? 'Include this sample' : 'Leave this sample out'}
                    aria-label={dropped ? `Include ${label}` : `Exclude ${label}`}
                  >
                    {dropped ? <Check size={14} /> : <X size={14} />}
                  </button>
                </div>

                {/* Collapsed, a sample shows its spread; that is usually enough
                    to tell whether a read went wrong. */}
                {!open && !dropped && (
                  <p className="flex flex-wrap gap-x-3 gap-y-1 px-9 pb-2 text-xs text-ink-400">
                    {fields.map((f) => {
                      const nums = reps
                        .map((r) => r.values[f.key])
                        .filter((v): v is number => typeof v === 'number');
                      if (!nums.length) return null;
                      const lo = Math.min(...nums);
                      const hi = Math.max(...nums);
                      const sd = summary ? reps[0].sd[f.key] : undefined;
                      return (
                        <span key={f.key} className="tabular-nums">
                          <span className="text-ink-500">{f.label}</span>{' '}
                          {lo === hi ? lo : `${lo}–${hi}`}
                          {typeof sd === 'number' ? ` ± ${sd}` : ''}
                          {f.unit ? ` ${f.unit}` : ''}
                        </span>
                      );
                    })}
                  </p>
                )}

                {open && (
                  <div className="overflow-x-auto border-t border-ink-800 px-2 py-2">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-ink-500">
                          {!summary && <th className="px-1 py-1 font-medium">#</th>}
                          {fields.map((f) => (
                            <Fragment key={f.key}>
                              <th className="px-1 py-1 font-medium">
                                {summary ? 'Mean ' : ''}
                                {f.label}
                                {f.unit ? ` (${f.unit})` : ''}
                              </th>
                              {summary && <th className="px-1 py-1 font-medium">SD</th>}
                            </Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reps.map((r) => (
                          <tr key={r.localId}>
                            {!summary && (
                              <td className="px-1 py-0.5 tabular-nums text-ink-500">{r.replicate}</td>
                            )}
                            {fields.map((f) => (
                              <Fragment key={f.key}>
                                <td className="px-1 py-0.5">
                                  <Input
                                    value={r.text[f.key] ?? ''}
                                    onChange={(e) => setValue(r.localId, f.key, e.target.value)}
                                    className="!w-24 !px-1.5 !py-0.5 text-right font-mono !text-xs tabular-nums"
                                    aria-label={
                                      summary
                                        ? `Mean ${f.label} of ${label}`
                                        : `${f.label} replicate ${r.replicate} of ${label}`
                                    }
                                  />
                                </td>
                                {summary && (
                                  <td className="px-1 py-0.5">
                                    <Input
                                      value={r.sdText[f.key] ?? ''}
                                      onChange={(e) => setSd(r.localId, f.key, e.target.value)}
                                      className="!w-20 !px-1.5 !py-0.5 text-right font-mono !text-xs tabular-nums"
                                      aria-label={`SD of ${f.label} of ${label}`}
                                    />
                                  </td>
                                )}
                              </Fragment>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {!summary && (
                      <button
                        onClick={() => addReplicate(label)}
                        className="mt-1.5 inline-flex items-center gap-1 px-1 text-xs text-accent-400 hover:underline"
                      >
                        <Plus size={12} />
                        Add replicate
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {manual && (
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            icon={<Plus size={14} />}
            onClick={addSample}
          >
            Add sample
          </Button>
        )}
      </div>

      <div className="border-t border-ink-800 p-3">
        {missingN.length > 0 && (
          <p className="mb-2 flex gap-1.5 rounded-lg border border-amber-800 bg-amber-950 p-2 text-xs text-amber-200">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              Enter N for {missingN.slice(0, 4).join(', ')}
              {missingN.length > 4 ? ` and ${missingN.length - 4} more` : ''} — how many
              measurements each average is from. The sheet did not show it.
            </span>
          </p>
        )}
        {error && (
          <p className="mb-2 rounded-lg border border-red-800 bg-red-950 p-2 text-xs text-red-200">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <Button variant="ghost" className="flex-1" onClick={onCancel} disabled={busy}>
            Discard
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            loading={busy}
            disabled={filled === 0 || missingN.length > 0}
            onClick={() => void confirm()}
          >
            {manual ? 'Save' : 'Confirm'} {filled} reading{filled === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  );
}
